// AUTO-GENERATED SEED — review before trusting. Rename keys (index→list/detail),
// refine params, confirm caller flags, add opt-in response types.
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute } from './define-admin-route';
import type { AdminVerbSpec, PolymorphicEntityGrant } from './define-admin-route';
import type { AnyOfGrant, Grant } from '../../isomorphic/auth/grants';
// Type-only imports (erased at build) — keep the registry client-bundle-safe.
import type { LotDetailPayload } from '../../schemas/responses/lot-detail';
import type { LotPatchResult } from '../../schemas/responses/lot-patch';
import type { LotStateWriteResult } from '../../schemas/responses/lot-state-write';
import type { LotAttachmentEditResult, LotMediaIngestResult } from '../../schemas/responses/lot-attachments';
import type { BatchCheckResponse, BatchEditSuccess } from '../../schemas/responses/lot-batch';
import type { LotPlaceInAuctionResponse } from '../../schemas/responses/lot-place-in-auction';
import type { LotTransitionPreviewPayload } from '../../schemas/responses/lot-transition-preview';
import type { LotBidderIdentityPayload } from '../../schemas/responses/lot-bidder-identity';
import type { LotQrTargetPayload } from '../../schemas/responses/lot-qr-target';
import type { SwapBuyerCheckDto, SwapLotBuyersDto } from '../../schemas/responses/swap-lot-buyer';
import type {
  LotPriceAdjustmentContextDto,
  LotPriceAdjustmentPreviewDto,
  LotPriceAdjustmentWriteDto,
} from '../../schemas/responses/lot-price-adjustment';

/**
 * Every role that can open a lot in LotEditModal (BL-1833 PR 3a; BL-2055
 * retired HammerAdjustmentModal, the other door): Admin from auction pages, Cataloger from /inventory,
 * Accounting from an invoice, a settlement or a customer. The lot's reads —
 * its detail, and the Bids tab's ledger, max bids, bidder identities
 * and lot-scoped live state — admit all of them. The Bids tab
 * half is BL-1876's ruling (Michael Lawrie, 2026-09-24): staff may see
 * de-anonymized bids and max-bid ceilings; the bidder side never does. Named once so the lot's reads cannot
 * drift apart, the way `AUCTION_SETTINGS_READ` holds the settings family.
 */
export const LOT_EDIT_MODAL_READ = {
  anyOf: ['lot.read', 'inventory.read', 'invoice.read', 'settlement.read'],
} as const satisfies AdminVerbSpec['grant'];

/**
 * The floor of every lot transition's permission set (BL-1833 PR 3a): any role
 * that can run SOME transition through `POST /api/lots/batch` — Catalogers
 * (`inventory.update`), Admins (`lot.update`, `lot.hammer`) and Accounting
 * (`lot.correct_sale`, reopen). The batch route narrows per action in its handler;
 * the transition preview admits the floor WITHOUT that narrowing, so any role
 * that can perform some lot transition can preview any of them — a read, and
 * in-tenant staff reads are not sensitive (BUG-1758; BL-1833 ruling). Named once
 * so the preview and the write it previews cannot drift apart.
 */
export const LOT_TRANSITION_FLOOR = {
  anyOf: ['inventory.update', 'lot.update', 'lot.hammer', 'lot.correct_sale'],
} as const satisfies AdminVerbSpec['grant'];

/**
 * The auction reads an auction's Lot Info tab needs (BL-2349, option B1 —
 * Michael Lawrie, 2026-10-08): its chrome, its detail and its snapshot
 * catch-up. A Cataloger opens that ONE tab to add lots, so `inventory.read`
 * joins `auction.read` here and nowhere else — the Bidders, registrations,
 * clerk and auction-note routes stay on `auction.read` alone (bidder contact
 * data and auction notes stay closed to them; BL-1833, BL-1668, BL-2283).
 */
export const AUCTION_LOT_INFO_READ = {
  anyOf: ['auction.read', 'inventory.read'],
} as const satisfies PolymorphicEntityGrant;

/**
 * Placing an EXISTING lot into an auction (copy / assign — "From inventory",
 * Relist), or relisting it back to inventory (BL-1871): an auctions-management
 * action (BL-1038's team decision), so Admins (via `auction.update`) and
 * clerking (`lot.hammer`) — never a Cataloger. The place-in-auction route
 * narrows to this set for any item that names a `sourceLotId` (BL-2349).
 */
export const PLACE_IN_AUCTION_GRANT = {
  anyOf: ['auction.update', 'lot.hammer'],
} as const satisfies PolymorphicEntityGrant;

/**
 * Creating a NEW lot inside an auction (BL-2349): the placement pair plus a
 * Cataloger's `inventory.update` — catalogers add lots straight into a sale
 * (Michael Lawrie, 2026-10-08). The floor of `placeInAuction`'s auction
 * target, and the grant on the create form's staged documents
 * (`draft_document`); the client gates the Lots tab's Add lot on the same set.
 */
export const CREATE_IN_AUCTION_GRANT = {
  anyOf: ['auction.update', 'lot.hammer', 'inventory.update'],
} as const satisfies PolymorphicEntityGrant;

/**
 * Swap buyer's grant (BL-2059): the check and the apply both declare it, and every
 * door that offers Swap buyer (the lot ⋯ menu, the bid record, the invoice lot ⋮
 * menu, both bulk bars) locks on it, so a door can never offer what the route refuses
 * (BUG-2155, ADR 062 — Access Grants). `lot.correct_sale` is invoices.manage.
 */
export const SWAP_BUYER_GRANT = 'lot.correct_sale' as const satisfies Grant;

/**
 * The Adjust hammer window's write (BL-2529): either side's grant opens it, and the
 * service narrows per side. The lot ⋯ menu locks Adjust hammer on this same pair.
 */
export const PRICE_ADJUSTMENT_WRITE_GRANT = {
  anyOf: ['invoice.update', 'settlement.adjust'],
} as const satisfies AnyOfGrant;

export const lots = {
  // BL-816: unified batch APIs feeding the shared bulk bar (Sequencing / Auction
  // Items / Inventory). `batch_check` returns per-distinct-action availability +
  // reasons (drives UI enable/disable); `batch` applies ONE action to ≤50 lots
  // atomically (all-or-nothing). BL-817 PR 2b rewired the Sequencing tab onto
  // this engine and removed the now-dead auction-scoped bulk-update/bulk-remove
  // routes; PR 2c (BL-1067) rewired the last surface — the Auction Items tab's
  // Seller + Move actions — and removed the final legacy `bulk_catalog` route.
  batch_check: defineAdminRoute({
      path: '/api/lots/batch-check',
      params: {},
      // Scoped: /inventory selects across every tenant the caller works in, and
      // the check drops foreign ids rather than refusing — so the gate hands the
      // service the permitted SET, not one tenant (BL-1833 PR 3a). Soft-deleted
      // lots stay checkable (Restore), which `records` would 404.
      methods: {
        POST: {
          grant: LOT_EDIT_MODAL_READ,
          tenant: { from: 'scoped' },
          response: responseType<BatchCheckResponse>(),
        },
      },
    }),
  batch: defineAdminRoute({
      path: '/api/lots/batch',
      params: {},
      // Scoped, with the FLOOR of every action's permission set as the grant
      // (BL-1833 PR 3a). The route narrows per action — dispose-no-sale to
      // clerking, reopen to invoices.manage, the two target-auction actions to
      // the target's tenant — because the grant depends on the body's action.
      // Consolidation residue: per-action permission narrowing in the handler —
      // Michael Lawrie — BL-1833 (with `/auctions/:id/transition`, PR 3c).
      methods: {
        POST: {
          grant: LOT_TRANSITION_FLOOR,
          tenant: { from: 'scoped' },
          response: responseType<BatchEditSuccess>(),
        },
      },
    }),
  // BL-2059 (PRJ-81 FIX-3b) — Swap buyer: a check then an all-or-nothing apply
  // over `lotIds` (ADR 026 — Server Orchestration, Rule 1b). The lot ⋮ menu and
  // the invoice row are a selection of one through both; BL-1810's single-lot
  // `/api/auctions/:id/post-auction/lots/:lotId/swap` was folded in and deleted
  // (ADR 044 — Must Consolidate). Every id must be a lot of one tenant the
  // caller belongs to; another tenant's lot is a 404 at the gate. `max` matches
  // SWAP_BUYER_MAX_LOTS. `lot.correct_sale` is invoices.manage (Admin, Accounting).
  swap_buyer_check: defineAdminRoute({
      path: '/api/lots/swap-buyer-check',
      params: {},
      methods: {
        POST: {
          grant: SWAP_BUYER_GRANT,
          tenant: { from: 'records', table: 'lot', field: 'lotIds', max: 50 },
          response: responseType<SwapBuyerCheckDto>(),
        },
      },
    }),
  swap_buyer: defineAdminRoute({
      path: '/api/lots/swap-buyer',
      params: {},
      methods: {
        POST: {
          grant: SWAP_BUYER_GRANT,
          tenant: { from: 'records', table: 'lot', field: 'lotIds', max: 50 },
          response: responseType<SwapLotBuyersDto>(),
        },
      },
    }),
  // BL-1287: the EFFECTS axis alongside `batch_check`'s eligibility axis. Given a
  // transition + a selection, returns the operator-facing disclosure computed
  // from live data by the same `Effect` objects that will perform the work — so
  // the confirmation modal cannot promise something different from what runs.
  // Read-only.
  transitionPreview: defineAdminRoute({
      path: '/api/lots/transition-preview',
      params: {},
      // The batch route's floor (BUG-1758): any role that can perform some lot
      // transition can preview any of them. Was clerking-only, so a Cataloger's
      // /inventory confirmations 403'd. The standing max-bid counts it discloses
      // are visible to every staff role (BL-1876). Scoped so a multi-tenant
      // caller's lots outside the permitted set are dropped from the counts
      // rather than 403ing the request.
      methods: {
        POST: {
          grant: LOT_TRANSITION_FLOOR,
          tenant: { from: 'scoped' },
          response: responseType<LotTransitionPreviewPayload>(),
        },
      },
    }),
  // BL-1038: the consolidated place-in-auction engine — copies/assigns ≤50
  // source lots into a target auction, atomic all-or-nothing
  // (`server-side-orchestration.md` Rule 1b): all items place or the
  // whole batch is rejected (409 with the blocking items) and nothing is
  // written. ONE write API behind all three "put this item in that auction"
  // doors: the Relist modal, the From-Inventory modal, and the bulk-bar "Assign
  // to auction". A source lot with `auction_id IS NULL` (first-ever listing) is
  // assigned in place (audit verb `updated`); any other source is copied
  // (verb `created`).
  placeInAuction: defineAdminRoute({
      path: '/api/lots/place-in-auction',
      params: {},
      // The TARGET names the tenant (read from the body). BL-1871 made it
      // polymorphic: an `auction` target resolves in the auction table (every
      // source lot must then belong to that tenant — the service checks each
      // one); an `inventory` target has no auction, so `targetId` is the lot
      // being relisted and its own row names the tenant. BL-2349: the auction
      // target's floor also admits a Cataloger, who may CREATE new lots there;
      // the handler narrows any existing-lot item back to the placement pair.
      methods: {
        POST: {
          grant: { none: 'per target kind — see tenant.entities' },
          tenant: {
            from: 'polymorphic',
            typeField: 'targetKind',
            idField: 'targetId',
            entities: {
              auction: { table: 'auction', grant: CREATE_IN_AUCTION_GRANT },
              inventory: { table: 'lot', grant: PLACE_IN_AUCTION_GRANT },
            },
          },
          response: responseType<LotPlaceInAuctionResponse>(),
        },
      },
    }),
  id: {
    // BL-2055 (PRJ-81 FIX-2) — the Adjust hammer window: a buyer-side hammer
    // correction is the lot's one Price adjustment on its invoice (ADR 061 —
    // Derived Hammer, as amended). LOT-keyed, unlike the invoice's own draft
    // routes, because both doors (the invoice's lot menu and the Lots page) know
    // only the lot: the service resolves the invoice from the lot's claim and
    // locks it under the gate's tenant, so another house's lot is a 404 here and
    // another house's invoice can never be reached through the body.
    // BL-2529 (PRJ-95 SETL-6) adds the seller side: the route takes either
    // side's grant and narrows per side — `invoice.update` for the buyer,
    // `settlement.adjust` for the seller (`assertCallerHoldsGrant`), and reads
    // only the sides the caller may read.
    price_adjustment: defineAdminRoute({
        path: '/api/lots/:id/price-adjustment',
        params: { id: '' },
        methods: {
          GET: {
            grant: { anyOf: ['invoice.read', 'settlement.read'] },
            tenant: { from: 'record', table: 'lot', param: 'id' },
            response: responseType<LotPriceAdjustmentContextDto>(),
            // BUG-2210: the invoice page the window opened from; a Void named here is its buyer side.
            query: { invoiceId: '' },
          },
          PUT: {
            grant: PRICE_ADJUSTMENT_WRITE_GRANT,
            tenant: { from: 'record', table: 'lot', param: 'id' },
            response: responseType<LotPriceAdjustmentWriteDto>(),
          },
        },
      }),
    price_adjustment_preview: defineAdminRoute({
        path: '/api/lots/:id/price-adjustment/preview',
        params: { id: '' },
        methods: {
          // BUG-2181 G5: the preview prices the same write its PUT twin
          // commits, so it carries the identical anyOf grant — not the
          // buyer-only `invoice.update`, which left a seller-side-only caller
          // able to see the write's price-adjustment form but refused at Save.
          POST: {
            grant: PRICE_ADJUSTMENT_WRITE_GRANT,
            tenant: { from: 'record', table: 'lot', param: 'id' },
            response: responseType<LotPriceAdjustmentPreviewDto>(),
          },
        },
      }),
    // BL-1542 AC 20 — the lot's bidders, with the identity the bid record joins
    // onto rows arriving on the admin bid-ledger / max-bid streams. LOT-keyed:
    // it replaces `/api/auctions/:id/lots/:lotId/bidder-identity` (deleted in
    // the same PR, ADR 044) so `LotBidRecord` can mount with a lot id alone.
    bidder_identity: defineAdminRoute({
        path: '/api/lots/:id/bidder-identity',
        params: { id: '' },
        methods: {
          GET: {
            grant: LOT_EDIT_MODAL_READ,
            tenant: { from: 'record', table: 'lot', param: 'id' },
            response: responseType<LotBidderIdentityPayload>(),
          },
        },
      }),
    // BL-2355 — where a scanned lot label takes this staff member: an admin
    // screen in the lot's house, or the lot's public page. `scoped`, not
    // `record`: a lot in a house the caller can't read answers `bidder`, never
    // the gate's 404 (ADR 062 — Access Grants). The handler validates `:id`.
    qr_target: defineAdminRoute({
        path: '/api/lots/:id/qr-target',
        params: { id: '' },
        methods: {
          GET: {
            grant: { anyOf: ['lot.read', 'inventory.read'] },
            tenant: { from: 'scoped' },
            response: responseType<LotQrTargetPayload>(),
          },
        },
      }),
    // BL-2338 (PRJ-91) — add or remove ONE photo/video or document, keyed by the
    // id the caller minted, so the offline queue can replay each as its own op.
    // The catalog-edit grant the PATCH's full-edit floor uses (Admin, Cataloger):
    // Accounting never edits a lot's photos or documents, on either door.
    media: defineAdminRoute({
      path: '/api/lots/:id/media',
      params: { id: '' },
      methods: {
        POST: {
          grant: 'inventory.update',
          tenant: { from: 'record', table: 'lot', param: 'id' },
          response: responseType<LotAttachmentEditResult>(),
        },
      },
    }),
    media_item: defineAdminRoute({
      path: '/api/lots/:id/media/:mediaId',
      params: { id: '', mediaId: '' },
      methods: {
        DELETE: {
          grant: 'inventory.update',
          tenant: { from: 'record', table: 'lot', param: 'id' },
          response: responseType<LotAttachmentEditResult>(),
        },
        // BL-2676 (ADR 070 — Ingest Protocol § 4): the phone's repeat-safe media ingest, one multipart
        // request per photo (or one video attach), named by the client media id in the path.
        PUT: {
          grant: 'inventory.update',
          tenant: { from: 'record', table: 'lot', param: 'id' },
          response: responseType<LotMediaIngestResult>(),
        },
      },
    }),
    // BL-2417 (PRJ-91) — put a lot's photos in a given order (the first is Main),
    // so the offline queue can replay a reorder or "Set as main" as one op. Same
    // grant and tenant as the per-item media add / remove beside it.
    media_order: defineAdminRoute({
      path: '/api/lots/:id/media/order',
      params: { id: '' },
      methods: {
        PUT: {
          grant: 'inventory.update',
          tenant: { from: 'record', table: 'lot', param: 'id' },
          response: responseType<LotAttachmentEditResult>(),
        },
      },
    }),
    // BL-2677 (ADR 070 — Ingest Protocol §§ 1, 2, 5): the phone's one-request lot write. The tenant comes from the
    // body, so the gate 404s a house the caller is not in and hands the handler gate.tenantId and gate.body.
    state: defineAdminRoute({
      path: '/api/lots/:id/state',
      params: { id: '' },
      methods: {
        PUT: {
          grant: 'inventory.update',
          tenant: { from: 'input', field: 'tenantId', in: 'body' },
          // 256 KiB: a 200-field-key body with money bases and 200 positions/removals is a few KiB, so this is
          // generous. The gate buffers the whole body to find the tenant, so the cap bounds that work.
          maxBodyBytes: 256 * 1024,
          response: responseType<LotStateWriteResult>(),
        },
      },
    }),
    documents: defineAdminRoute({
      path: '/api/lots/:id/documents',
      params: { id: '' },
      methods: {
        POST: {
          grant: 'inventory.update',
          tenant: { from: 'record', table: 'lot', param: 'id' },
          response: responseType<LotAttachmentEditResult>(),
        },
      },
    }),
    document: defineAdminRoute({
      path: '/api/lots/:id/documents/:documentId',
      params: { id: '', documentId: '' },
      methods: {
        DELETE: {
          grant: 'inventory.update',
          tenant: { from: 'record', table: 'lot', param: 'id' },
          response: responseType<LotAttachmentEditResult>(),
        },
      },
    }),
    index: defineAdminRoute({
        path: '/api/lots/:id',
        params: { id: '' },
        // Response DTO carrier (ADR 027 / BL-855): the client infers
        // LotDetailPayload with no <T>, and `respond()` type-checks the route
        // against this same declaration.
        // BL-950: PATCH is the lot-catalog edit — the write path for the inventory
        // editor now that catalog lives on the lot (works for home lots too).
        methods: {
          GET: {
            grant: LOT_EDIT_MODAL_READ,
            tenant: { from: 'record', table: 'lot', param: 'id' },
            response: responseType<LotDetailPayload>(),
          },
          // The FLOOR (BL-2318): inventory.manage (Admin and Cataloger) edits every
          // field; `lot.update_accounting` (Accounting) admits the request, and the
          // handler then narrows its body to tax, load-out fee and title paperwork
          // (`lot-edit-scope.ts`, access-grants.md § 6). BL-1870: the response
          // DTO is threaded (ADR 027) — both doors return the lot's id.
          PATCH: {
            grant: { anyOf: ['inventory.update', 'lot.update_accounting'] },
            tenant: { from: 'record', table: 'lot', param: 'id' },
            response: responseType<LotPatchResult>(),
          },
        },
      }),
  },
};
